import { createRef, Component } from "react";
import classNames from "classnames";
import {observer} from "mobx-react";
import Router from 'next/router';
import styles from "./styles/megaNav.module.scss";
import MainLink from "./MainLink";
import Dropdown from "./Dropdown";
import NavigationModel from "../../../../utils/NavigationModel";
import Button from "../../../utilities/Button";
import FlatIcon from "../../../utilities/FlatIcon";
import memoize from "memoize-one";
import PropTypes from 'prop-types';
import {StoreContext} from "../../../../stores/StoreLoader";
import FontAwesome from "../../../utilities/FontAwesome";
import ApiProgressIndicator from "../../ApiProgressIndicator";
import HelpScout from "../../../support/HelpScout";
import {getDistrictUrl} from "../../../../utils/SchoolBlocksUtilities";
import {reaction} from "mobx";

@observer
export default class MegaNav extends Component {

    static contextType = StoreContext;

    static propTypes = {
        setNavButtonRefs: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.mainLinkRefs = new Map();

        this.state = {
            activeDropdown: null,
            availableYAbove: null,
            availableYBelow: null,
            activeMainlink: null,
        };

        this.hoverTime = 250;
        this.hoverTimeout = null;
        this.hoverLeaveTimeout = null;

        this.containerRef = createRef();
    }

    componentDidMount() {
        this.disposer = reaction(() => this.context.interfaceStore.currentFullUrl, this.closeDropdown);
    }

    componentWillUnmount() {
        this.disposer();
    }

    openDropdown = (evt, link, keypress) => {
        const {interfaceStore} = this.context;
        if (evt && link.children.length > 0) {
            const rect = evt.target.getBoundingClientRect();
            const preservedValues = {
                activeDropdown: link.id,
                activeMainlink: this.mainLinkRefs.get(link.id).getDomElement(),
                availableYAbove: rect.y,
                availableYBelow: interfaceStore.dimensions.height - rect.y, // need to know open space below current = total - y
                keypress, // when keypress is on, it will automatically focus on the first dropdown link when dropdown is opened
            };
            this.hoverLeaveTimeout = setTimeout(() => {
                this.setState(preservedValues);
            }, this.hoverTime);

        }
        clearTimeout(this.hoverTimeout);
    };

    closeDropdown = () => {
        this.hoverTimeout = setTimeout(() => {
            this.setState({
                activeDropdown: null,
            });
        }, this.hoverTime);
        clearTimeout(this.hoverLeaveTimeout);
    };

    handleAddNewLink = () => {
        Router.push(`${typeof window !== "undefined" && window.location.pathname}?spview=Navigation`);

        this.context.modalStore.addModal({
            type: "admin",
            id: 'navigation-modal',
            condition: 1,
            nodeInfo: new NavigationModel(),
        })
    };

    setButtonStyles = memoize(currentBlock => {
        this.props.navButtonRefs.forEach((button, key) => {
            if (button && currentBlock && key === currentBlock) {
                button.classList.add(styles.highlight);
            } else if (button) {
                button.classList.remove(styles.highlight);
            }
        });
    });

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.currentBlock !== prevProps.currentBlock) {
            this.setButtonStyles(this.props.currentBlock);
        }
    }

    render() {
        const {organizationStore, userStore, styleStore} = this.context;

        const mainLinkClassName = classNames({
            [styles.mainLink]: true,
        });
        const addNewLinkClassName = classNames({
            [styles.addNewLink]: true,
        });

        let linkObjs;
        if (organizationStore.navigation.length > 0) {
            linkObjs = organizationStore.navigationTree
                .filter(item => item.enabled)
                .map(item => {
                    let linkEvents = {};
                    if (item.children.length > 0) {
                        linkEvents = {
                            onMouseEnter: e => {
                                this.openDropdown(e, item);
                            },
                            onTouchStart: e => {
                                if (this.state.activeDropdown !== item.id) {
                                    this.openDropdown(e, item);
                                } else {
                                    this.closeDropdown();
                                }
                            },
                            onKeyDown: (e) => {
                                if (e.which === 13 || e.which === 40) { // enter or down arrow
                                    e.preventDefault();
                                    this.openDropdown(e, item, true);
                                }
                            },
                            onMouseLeave: () => {
                                this.closeDropdown();
                            },
                        }
                    }
                    return <MainLink
                        expanded={item.id === this.state.activeDropdown && item.children.length > 0}
                        hasPopup={item.children.length > 0}
                        key={item.id}
                        ref={r => this.mainLinkRefs.set(item.id, r)}
                        events={linkEvents || {}}
                        navigationObj={item}
                    />;
                });
        } else {

            linkObjs = Array.from(organizationStore.blocks.values()).filter(block => {
                return organizationStore.block_config.get(block.key);
            })
                .map(item => {
                    return <li ref={r => {
                        if (this.props.setNavButtonRefs) {
                            this.props.setNavButtonRefs(item.key, r);
                        }
                    }} key={item.id}
                               className={mainLinkClassName}>
                        <Button onClick={() => this.context.interfaceStore.scrollToId(item.key)}>
                            {item.title}
                        </Button>
                    </li>
                });

            if (userStore.isAdmin && !organizationStore.customNavigationIsDisabled) {
                linkObjs.push(
                    <li key={'add-link'} className={mainLinkClassName}>
                        <Button className={addNewLinkClassName} onClick={this.handleAddNewLink}><FlatIcon
                            name={'flaticon-add'}/> Add Link</Button>
                    </li>
                )
            }
        }

        const dropDownEvents = { // dropdown events (stored here)
            onMouseEnter: () => {
                this.openDropdown();
            },
            onMouseLeave: () => {
                this.closeDropdown();
            },
        };

        const cdMorphDropdownClassName = classNames({
            [styles.cdMorphDropdown]: true,
        });
        const mainNavClassName = classNames({
            [styles.mainNav]: true,
            ['sb-content-width-plus-margin']: styleStore.themeID === '1',
        });

        const districtUrl = getDistrictUrl(organizationStore.organization);

        return (
            <>
                <div ref={this.containerRef} className={cdMorphDropdownClassName}>
                    <nav className={mainNavClassName} id={'navigation'} role={"navigation"}>
                        {districtUrl && <ul style={{float: "left"}}>
                            <li className={styles.mainLink}>
                                <FontAwesome prefix={'fas'} name={'fa-university'}/>
                                <a href={districtUrl}>
                                    District
                                </a>
                            </li>
                        </ul>}
                        <ul style={{float: districtUrl ? "right" : "none"}}>
                            {linkObjs}
                        </ul>
                    </nav>
                    {this.state.activeDropdown && <Dropdown
                        activeDropdown={this.state.activeDropdown}
                        events={dropDownEvents}
                        activeMainlink={this.state.activeMainlink}
                        availableYAbove={this.state.availableYAbove}
                        availableYBelow={this.state.availableYBelow}
                        containerRef={this.containerRef}
                        closeDropdown={this.closeDropdown}
                        keypress={this.state.keypress}
                    />}
                </div>
                <ApiProgressIndicator />
                <HelpScout />
            </>
        );
    }
}
